// Copyright (C) 2021 Bystronic Laser AG

import i18n from '@/i18n';
import { normalizeString } from './string';
import { getFieldPathValue, isEmpty, isNil } from './misc';
import moment from 'moment';
import { User } from '@/models/user';

export function generateLocalizedCustomSearch<T>(
  key: string,
  localizationKeyFn: (value: string) => string,
): (item: any, searchTerm: string) => boolean {
  return (item, searchTerm) => {
    if (isEmpty(item[key])) {
      return false;
    }
    const localizationKey = localizationKeyFn(String(item[key]));
    const normalizedTranslatedValue = normalizeString(
      i18n.t(localizationKey).toString(),
    );
    return normalizedTranslatedValue.includes(normalizeString(searchTerm));
  };
}

export function searchByDateField(
  key: string,
  formatDateFunction: (date: Date | null) => string,
): (item: any, searchTerm: string) => boolean {
  return (item, searchTerm) => {
    if (isEmpty(formatDateFunction(item[key]))) {
      return false;
    }
    return matchIgnoringCase(formatDateFunction(item[key]), searchTerm);
  };
}

export function matchIgnoringCase(
  cellText: string | null,
  searchText: string,
): boolean {
  return !!cellText
    ?.toLowerCase()
    .trim()
    .includes(searchText.toLowerCase().trim());
}

export function matchesDate(
  date: Date | null,
  searchText: string,
  formatter: (date: Date) => string = formatDate,
): boolean {
  const formattedDate = isNil(date) ? '' : formatter(date);
  return matchIgnoringCase(formattedDate, searchText);
}

export function formatDate(date: Date | null): string {
  if (isNil(date)) {
    return '';
  }

  return moment(date).format('DD/MM/YYYY');
}

export type FilterMatcher = (row: any, filterValue: any) => boolean;

export function createTextListMatcher<T>(fieldPath: string): FilterMatcher {
  return (row: T, filterValue: string[]) =>
    textListMatches(getFieldPathValue(row, fieldPath) as string, filterValue);
}

export function createTextListMatcherForEnum<T>(
  fieldPath: string,
  enumTranslator: (enumValue: any) => string,
): FilterMatcher {
  return (row: T, filterValue: string[]) =>
    textListMatches(
      enumTranslator(getFieldPathValue(row, fieldPath)),
      filterValue,
    );
}

export function textListMatches(
  rowValue: string,
  filterValue: string[],
): boolean {
  if (isEmpty(filterValue)) {
    return true;
  }

  return filterValue.some((value: string) =>
    matchIgnoringCase(rowValue, value),
  );
}

export function createArrayMatcher<T>(fieldPath: string): FilterMatcher {
  return (row: T, filterValue: string[]) => {
    const arrayValue = getFieldPathValue(row, fieldPath) as
      | string[]
      | undefined;

    if (isEmpty(arrayValue)) {
      return isEmpty(filterValue);
    }

    // If filterValue is empty, return true to match all rows (no filtering applied)
    if (isEmpty(filterValue)) {
      return true;
    }

    // Check if any element in the array matches any value in the filter array
    return arrayValue!.some((arrayItem) =>
      filterValue.some((filterItem) =>
        matchIgnoringCase(arrayItem, filterItem),
      ),
    );
  };
}

/**
 * Returns a matcher matching list of numbers as strings against a numeric value
 * from a row field.
 *
 * - Matches exact values (no partial matching).
 * - Matches multiple values with OR.
 * - Ignores invalid filter values.
 * - Coalesces null row values into 0.
 *
 * @param fieldPath Path to the field in the row object as a dot separated
 *   string ('field1.subfield2.subfield3').
 */
export function createTextListMatcherForNumber<T>(
  fieldPath: string,
): FilterMatcher {
  return (row: T, filterValue: string[]) =>
    numberListMatches(
      getFieldPathValue(row, fieldPath) ?? 0,
      filterValue.map((value) => +value),
    );
}

/**
 * Returns a matcher matching list of numbers as strings against a numeric value
 * from a row field that has been adapted by uiAdapter.
 *
 * This matcher is to be used when there's some kind of transformation applied
 * to the value shown in the table, so the user can enter values in the filter
 * as seen on the table. For example, when there's some formatting, unit
 * conversion or similar that changes the original value.
 *
 * - Matches exact values (no partial matching).
 * - Matches multiple values with OR.
 * - Ignores invalid filter values.
 * - Coalesces null row values into 0.
 *
 * @param fieldPath Path to the field in the row object as a dot separated
 *   string ('field1.subfield2.subfield3').
 * @param uiAdapter Function applying some transformation to the row value
 *   before being matched.
 */
export function createTextListMatcherForUiNumber<T>(
  fieldPath: string,
  uiAdapter: (value: number) => string,
): FilterMatcher {
  return (row: T, filterValue: string[]) =>
    numberListMatches(
      uiAdapter(getFieldPathValue(row, fieldPath) ?? 0),
      filterValue,
    );
}

function numberListMatches(
  rowValue: string | number,
  filterValue: number[] | string[],
): boolean {
  if (isEmpty(filterValue)) {
    return true;
  }

  return filterValue.some((value) => rowValue === value);
}

export function createDateRangeMatcher<T>(fieldPath: string): FilterMatcher {
  return (row: T, filterValue: [Date, Date] | null | undefined) => {
    if (isNil(filterValue)) {
      return true;
    }

    const value: Date | null = getFieldPathValue(row, fieldPath);

    if (isNil(value)) {
      return false;
    }

    const dateRangeEnd = moment(filterValue[1]).endOf('day').valueOf();

    return (
      value.getTime() >= filterValue[0].getTime() &&
      value.getTime() <= dateRangeEnd
    );
  };
}

export function formatDateWithTime(date: Date | null): string {
  if (isNil(date)) {
    return '';
  }

  return moment(date).format('DD/MM/YYYY, HH:mm');
}

/**
 * Function to add a data-testid attribute to a table column.
 *
 * To be passed to a b-table-column's th-attrs property. Only used for unit
 * tests.
 **/
export function addDataTestIdAttributeToTh(id: string) {
  return () => ({
    'data-testid': id,
  });
}

export function filterUserType(userItem: User, filter: string) {
  const translatedType = i18n.t(
    `user.user_type.${userItem.userType}`,
  ) as string;
  return matchIgnoringCase(translatedType, filter);
}

export function sortByUserType(a: User, b: User, isAsc: boolean) {
  const translatedTypeA = i18n.t(`user.user_type.${a.userType}`) as string;
  const translatedTypeB = i18n.t(`user.user_type.${b.userType}`) as string;
  return isAsc
    ? translatedTypeA.localeCompare(translatedTypeB)
    : translatedTypeB.localeCompare(translatedTypeA);
}
